/**
 * hooks/useClientForm.ts
 *
 * Custom hook that encapsulates all state, data loading, and event handling
 * for the client form filling experience (ClientCollect page).
 *
 * Exposes: loading state, request data, navigation, response management,
 * file upload, section transitions, and computed values.
 *
 * Updated June 2026 (refactor):
 * - Removed "Remplir plus tard" / skipped fields feature entirely.
 *   Required fields now block navigation until filled (strict linear flow).
 *   The auto-save mechanism is the safety net for clients who need to leave
 *   mid-form and resume later via the original link.
 * - canProceed now returns false for required empty fields (non-system, non-info).
 * - handleNext simplified: no skipped field tracking.
 * - isSaving: true while saveResponse is in flight — drives the persistent SaveIndicator.
 * - showQuitScreen: when true, renders the SaveAndQuitScreen instead of the form.
 * - System email field remains strictly blocking (is_system check in canProceed).
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { buildStepSequence, findInitialStep, isFieldVisible } from '../utils/stepSequenceBuilder';
import type { StepItem } from '../utils/stepSequenceBuilder';
import type { PreviewData, RequestField } from '../utils/previewHelpers';
import { captureError } from '../utils/errorReporting';
import { getSenderDisplayName } from '../utils/brandingHelpers';

const MAX_FILES_PER_FIELD = 10;

interface Request {
  id: string;
  title: string;
  description: string | null;
  custom_message: string | null;
  status: string;
  access_token?: string;
}

export interface UploadedFileEntry {
  id: string;
  file: File;
  url?: string;
  storagePath?: string;
  uploaded: boolean;
  uploading: boolean;
  error?: string;
}

interface UseClientFormParams {
  token: string | undefined;
  previewMode: boolean;
  previewData?: PreviewData;
}

export function useClientForm({ token, previewMode, previewData }: UseClientFormParams) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [request, setRequest] = useState<Request | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [senderDisplayName, setSenderDisplayName] = useState<string | null>(null);
  const [allFields, setAllFields] = useState<RequestField[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [showWelcome, setShowWelcome] = useState(true);
  const [showSectionTransition, setShowSectionTransition] = useState(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFileEntry[]>>({});
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showQuitScreen, setShowQuitScreen] = useState(false);

  const navigatingRef = useRef(false);
  const mountedRef = useRef(true);
  const requestRef = useRef<Request | null>(null);
  const cancelledFileIdsRef = useRef<Set<string>>(new Set());
  const responsesRef = useRef<Record<string, string>>({});
  const allFieldsRef = useRef<RequestField[]>([]);
  const currentStepRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    responsesRef.current = responses;
  }, [responses]);

  useEffect(() => {
    allFieldsRef.current = allFields;
  }, [allFields]);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const stepSequence = useMemo(
    () => buildStepSequence(allFields, responses),
    [allFields, responses]
  );

  const isLastStep = currentStep === stepSequence.length - 1;

  const loadPreviewData = useCallback(() => {
    if (!previewData) return;
    try {
      setRequest({
        id: 'preview-' + Date.now(),
        title: previewData.title,
        description: previewData.description,
        custom_message: previewData.customMessage,
        status: 'preview',
      });
      setLogoUrl(previewData.logoUrl);
      setAllFields(previewData.fields);
      setShowWelcome(true);
      setLoading(false);
    } catch (err) {
      console.error('Error loading preview data:', err);
      setError("Une erreur est survenue lors du chargement de l'aperçu");
      setLoading(false);
    }
  }, [previewData]);

  const loadRequest = useCallback(async () => {
    if (!token) return;
    try {
      const [{ data: requestData, error: requestError }, { data: brandingRows, error: brandingError }] = await Promise.all([
        supabase.from('requests').select('*').eq('access_token', token).maybeSingle(),
        supabase.rpc('get_request_sender_branding', { p_access_token: token }),
      ]);

      if (brandingError) {
        console.error('Error loading sender branding:', brandingError);
        captureError(brandingError, { hook: 'useClientForm', action: 'loadRequestBranding', hasToken: Boolean(token) });
      }
      if (requestError) throw requestError;
      if (!requestData) {
        setError('Formulaire introuvable ou lien invalide');
        setLoading(false);
        return;
      }
      const branding = Array.isArray(brandingRows) ? (brandingRows[0] as {
        logo_url: string | null;
        first_name: string | null;
        last_name: string | null;
      } | undefined) : null;
      setLogoUrl(branding?.logo_url ?? null);
      setSenderDisplayName(
        branding
          ? getSenderDisplayName({ first_name: branding.first_name, last_name: branding.last_name })
          : null
      );

      if (requestData.status === 'draft') {
        setError("Ce formulaire n'a pas encore été envoyé");
        setLoading(false);
        return;
      }
      if (requestData.status === 'completed') {
        setSubmitted(true);
        setLoading(false);
        return;
      }
      const req: Request = {
        id: requestData.id,
        title: requestData.title,
        description: requestData.description,
        custom_message: requestData.custom_message,
        status: requestData.status,
        access_token: requestData.access_token,
      };
      setRequest(req);

      const [fieldsResult, responsesResult, docsResult] = await Promise.all([
        supabase.from('request_fields').select('*').eq('request_id', requestData.id).order('position'),
        supabase.from('request_responses').select('field_id, value').eq('request_id', requestData.id),
        supabase.from('documents').select('field_id').eq('request_id', requestData.id).eq('upload_status', 'completed'),
      ]);

      if (fieldsResult.error) throw fieldsResult.error;

      const enrichedFields: RequestField[] = (fieldsResult.data || []).map((f) => {
        const rawConditions = f.conditions;
        const newConditions =
          Array.isArray(rawConditions) && rawConditions.length > 0
            ? (rawConditions as Array<{ source_field_id: string; values: string[] }>)
            : null;
        const legacyCondition =
          !newConditions && f.condition_source_field_id
            ? [{
                source_field_id: f.condition_source_field_id,
                values: Array.isArray(f.condition_values) ? (f.condition_values as string[]) : [],
              }]
            : null;
        return {
          id: f.id,
          type: f.type,
          label: f.label,
          description: f.description,
          is_required: f.is_required,
          position: f.position,
          options: f.options as string[] | null,
          conditions: newConditions || legacyCondition,
          condition_operator: (f.condition_operator as 'or' | 'and') || 'or',
          section: f.section,
          is_system: (f as { is_system?: boolean }).is_system ?? false,
        };
      });

      setAllFields(enrichedFields);

      const savedResponses: Record<string, string> = Object.fromEntries(
        (responsesResult.data || []).map((r) => [r.field_id, r.value])
      );
      setResponses(savedResponses);

      const savedDocIds = new Set((docsResult.data || []).map((d) => d.field_id as string));
      const initialSequence = buildStepSequence(enrichedFields, savedResponses);
      const initialStep = findInitialStep(initialSequence, savedResponses, savedDocIds);
      setCurrentStep(initialStep);

      if (initialStep > 0) {
        setShowWelcome(false);
      }

      if (requestData.status === 'sent') {
        try {
          await supabase.from('requests').update({ status: 'in_progress' }).eq('id', requestData.id);
        } catch (statusErr) {
          console.warn('Could not update request status to in_progress:', statusErr);
        }
      }

      supabase.rpc('mark_request_opened', { p_access_token: token }).then(({ error: openErr }) => {
        if (openErr) console.warn('mark_request_opened failed (non-blocking):', openErr);
      }).catch((err) => console.warn('mark_request_opened threw (non-blocking):', err));

      setLoading(false);
    } catch (err) {
      console.error('Error loading request:', err);
      captureError(err, { hook: 'useClientForm', action: 'loadRequest', hasToken: Boolean(token) });
      setError('Une erreur est survenue lors du chargement du formulaire');
      setLoading(false);
    }
  }, [token]);

  const cleanupHiddenFieldResponses = useCallback(async (hiddenIds: string[]) => {
    if (hiddenIds.length === 0 || previewMode || !requestRef.current) return;
    const requestId = requestRef.current.id;
    try {
      const { data: docRows, error: docQueryError } = await supabase
        .from('documents')
        .select('storage_path')
        .eq('request_id', requestId)
        .in('field_id', hiddenIds);
      if (docQueryError) console.error('Error querying documents for cleanup:', docQueryError);
      const storagePaths = (docRows || []).map((d) => d.storage_path as string).filter(Boolean);
      if (storagePaths.length > 0) {
        const { error: storageError } = await supabase.storage.from('request-documents').remove(storagePaths);
        if (storageError) console.error('Error deleting files from storage during cleanup:', storageError);
      }
      await Promise.all([
        supabase.from('documents').delete().eq('request_id', requestId).in('field_id', hiddenIds),
        supabase.from('request_responses').delete().eq('request_id', requestId).in('field_id', hiddenIds),
      ]);
    } catch (err) {
      console.error('Error during hidden field cleanup:', err);
    }
  }, [previewMode]);

  const handleResponseChange = useCallback((fieldId: string, value: string) => {
    const newResponses = { ...responses, [fieldId]: value };
    const fieldsMap = new Map(allFields.map((f) => [f.id, f]));
    const nowHiddenIds = allFields
      .filter((f) => {
        const wasVisible = isFieldVisible(f.id, fieldsMap, responses);
        const isNowVisible = isFieldVisible(f.id, fieldsMap, newResponses);
        return wasVisible && !isNowVisible;
      })
      .map((f) => f.id);

    if (nowHiddenIds.length > 0) {
      nowHiddenIds.forEach((id) => { delete newResponses[id]; });
      setUploadedFiles((prev) => {
        const next = { ...prev };
        nowHiddenIds.forEach((id) => {
          (prev[id] || []).forEach((entry) => {
            if (entry.uploading) cancelledFileIdsRef.current.add(entry.id);
          });
          delete next[id];
        });
        return next;
      });
    }

    setResponses(newResponses);

    if (nowHiddenIds.length > 0) {
      cleanupHiddenFieldResponses(nowHiddenIds);
    }
  }, [allFields, responses, cleanupHiddenFieldResponses]);

  const uploadSingleFile = useCallback(async (fieldId: string, fileId: string, file: File): Promise<void> => {
    const currentRequest = requestRef.current;

    if (previewMode) {
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          if (!mountedRef.current) { resolve(); return; }
          if (cancelledFileIdsRef.current.has(fileId)) {
            cancelledFileIdsRef.current.delete(fileId);
            resolve();
            return;
          }
          setUploadedFiles((prev) => {
            const arr = [...(prev[fieldId] || [])];
            const idx = arr.findIndex((e) => e.id === fileId);
            if (idx !== -1) {
              arr[idx] = { ...arr[idx], url: 'preview-url', uploaded: true, uploading: false };
            }
            return { ...prev, [fieldId]: arr };
          });
          resolve();
        }, 600);
      });
      return;
    }

    if (!currentRequest) {
      setUploadedFiles((prev) => {
        const arr = [...(prev[fieldId] || [])];
        const idx = arr.findIndex((e) => e.id === fileId);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], uploading: false, error: 'Une erreur est survenue' };
        }
        return { ...prev, [fieldId]: arr };
      });
      return;
    }

    try {
      const dotIndex = file.name.lastIndexOf('.');
      const fileExt = dotIndex !== -1 ? file.name.slice(dotIndex + 1) : '';
      const baseName = `${currentRequest.id}/${fieldId}_${Date.now()}_${fileId}`;
      const fileName = fileExt ? `${baseName}.${fileExt}` : baseName;

      if (cancelledFileIdsRef.current.has(fileId)) {
        cancelledFileIdsRef.current.delete(fileId);
        return;
      }

      const { error: uploadError } = await supabase.storage.from('request-documents').upload(fileName, file);
      if (uploadError) throw uploadError;

      if (cancelledFileIdsRef.current.has(fileId) || !mountedRef.current) {
        cancelledFileIdsRef.current.delete(fileId);
        try {
          await supabase.storage.from('request-documents').remove([fileName]);
        } catch (cleanupErr) {
          console.error('Error cleaning up cancelled storage file:', cleanupErr);
        }
        return;
      }

      const { data: { publicUrl } } = supabase.storage.from('request-documents').getPublicUrl(fileName);
      const { error: insertError } = await supabase.from('documents').insert({
        request_id: currentRequest.id,
        field_id: fieldId,
        original_filename: file.name,
        storage_path: fileName,
        file_size: file.size,
        mime_type: file.type,
        upload_status: 'completed',
      });
      if (insertError) {
        try {
          await supabase.storage.from('request-documents').remove([fileName]);
        } catch (rollbackErr) {
          console.error('Error rolling back storage file after insert failure:', rollbackErr);
        }
        throw insertError;
      }

      if (cancelledFileIdsRef.current.has(fileId) || !mountedRef.current) {
        cancelledFileIdsRef.current.delete(fileId);
        try {
          await Promise.all([
            supabase.storage.from('request-documents').remove([fileName]),
            supabase.from('documents').delete().eq('storage_path', fileName),
          ]);
        } catch (cleanupErr) {
          console.error('Error cleaning up cancelled upload after DB insert:', cleanupErr);
        }
        return;
      }

      setUploadedFiles((prev) => {
        const arr = [...(prev[fieldId] || [])];
        const idx = arr.findIndex((e) => e.id === fileId);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], url: publicUrl, storagePath: fileName, uploaded: true, uploading: false };
        }
        return { ...prev, [fieldId]: arr };
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      captureError(err, { hook: 'useClientForm', action: 'uploadSingleFile', fieldId });
      if (!mountedRef.current) return;
      setUploadedFiles((prev) => {
        const arr = [...(prev[fieldId] || [])];
        const idx = arr.findIndex((e) => e.id === fileId);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], uploading: false, error: "Erreur lors de l'upload" };
        }
        return { ...prev, [fieldId]: arr };
      });
    }
  }, [previewMode]);

  const handleFileSelect = useCallback((fieldId: string, files: File[]) => {
    const allowed = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/webp',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const currentFiles = uploadedFiles[fieldId] || [];
    const remaining = MAX_FILES_PER_FIELD - currentFiles.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_FILES_PER_FIELD} fichiers par champ`);
      return;
    }

    const filesToAdd = files.slice(0, remaining);
    const invalidSize = filesToAdd.find((f) => f.size > 10 * 1024 * 1024);
    if (invalidSize) { setError('Un fichier dépasse la taille maximale (10 MB)'); return; }
    const invalidType = filesToAdd.find((f) => !allowed.includes(f.type));
    if (invalidType) { setError('Format de fichier non supporté'); return; }

    setError('');

    const newIds = filesToAdd.map(() => crypto.randomUUID());

    setUploadedFiles((prev) => {
      const existing = prev[fieldId] || [];
      const newEntries: UploadedFileEntry[] = filesToAdd.map((file, i) => ({
        id: newIds[i],
        file,
        uploaded: false,
        uploading: true,
      }));
      return { ...prev, [fieldId]: [...existing, ...newEntries] };
    });

    filesToAdd.forEach((file, i) => {
      uploadSingleFile(fieldId, newIds[i], file);
    });
  }, [uploadedFiles, uploadSingleFile]);

  const handleFileRemove = useCallback(async (fieldId: string, fileIndex: number) => {
    const entry = uploadedFiles[fieldId]?.[fileIndex];
    if (!entry) return;

    if (!previewMode && entry.uploaded && entry.storagePath) {
      const { error: storageError } = await supabase.storage
        .from('request-documents')
        .remove([entry.storagePath]);
      if (storageError) console.error('Error deleting file from storage:', storageError);

      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('storage_path', entry.storagePath);
      if (dbError) console.error('Error deleting document record:', dbError);
    }

    if (!mountedRef.current) return;
    setUploadedFiles((prev) => {
      const arr = [...(prev[fieldId] || [])];
      const idx = arr.findIndex((e) => e.id === entry.id);
      if (idx === -1) return prev;
      arr.splice(idx, 1);
      if (arr.length === 0) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return { ...prev, [fieldId]: arr };
    });
  }, [uploadedFiles, previewMode]);

  const retryFileUpload = useCallback((fieldId: string, fileIndex: number) => {
    const fileEntry = uploadedFiles[fieldId]?.[fileIndex];
    if (!fileEntry) return;
    setUploadedFiles((prev) => {
      const arr = [...(prev[fieldId] || [])];
      const idx = arr.findIndex((e) => e.id === fileEntry.id);
      if (idx !== -1) {
        arr[idx] = { ...arr[idx], uploading: true, error: undefined };
      }
      return { ...prev, [fieldId]: arr };
    });
    uploadSingleFile(fieldId, fileEntry.id, fileEntry.file);
  }, [uploadedFiles, uploadSingleFile]);

  const saveResponse = async (fieldId: string, value: string): Promise<boolean> => {
    if (previewMode) return true;
    const currentRequest = requestRef.current;
    if (!currentRequest) {
      setError('Une erreur est survenue');
      return false;
    }
    setIsSaving(true);
    try {
      if (value.trim() === '') {
        const { error: deleteError } = await supabase
          .from('request_responses')
          .delete()
          .eq('request_id', currentRequest.id)
          .eq('field_id', fieldId);
        if (deleteError) throw deleteError;
      } else {
        const { error: saveError } = await supabase.from('request_responses').upsert(
          { request_id: currentRequest.id, field_id: fieldId, value },
          { onConflict: 'request_id,field_id' }
        );
        if (saveError) throw saveError;
      }
      return true;
    } catch (err) {
      console.error('Error saving response:', err);
      captureError(err, { hook: 'useClientForm', action: 'saveResponse', fieldId, requestId: requestRef.current?.id });
      setError('Erreur lors de la sauvegarde de la réponse');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const isValidEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  const persistRespondentEmail = async (field: { is_system?: boolean }, value: string | undefined): Promise<boolean> => {
    if (!field.is_system || !value || previewMode || !requestRef.current) return true;
    const normalizedEmail = value.trim().toLowerCase();
    if (normalizedEmail === '') return true;
    const { error: emailError } = await supabase
      .from('requests')
      .update({ respondent_email: normalizedEmail })
      .eq('id', requestRef.current.id);
    if (emailError) {
      captureError(emailError, { hook: 'useClientForm', action: 'persistRespondentEmail', requestId: requestRef.current.id });
      setError('Une erreur est survenue lors de la sauvegarde de votre adresse email');
      return false;
    }
    return true;
  };

  // Required non-system non-info fields block navigation until filled.
  // Optional fields always allow advancing (shown as "Passer" in QuestionCard).
  const canProceed = (): boolean => {
    const step = stepSequence[currentStep];
    if (!step) return false;
    const field = step.field;
    if (field.type === 'info') return true;
    if (field.is_system) {
      return isValidEmail(responses[field.id] ?? '');
    }
    if (field.type === 'document') {
      const files = uploadedFiles[field.id] || [];
      if (files.some((f) => f.uploading)) return false;
      if (field.is_required) {
        return files.some((f) => f.uploaded);
      }
      return true;
    }
    if (field.is_required) {
      return (responses[field.id] ?? '').trim().length > 0;
    }
    return true;
  };

  const checkSectionTransition = (seq: StepItem[], step: number, nextIndex: number): boolean => {
    if (nextIndex >= seq.length) return false;
    const cur = seq[step];
    const next = seq[nextIndex];
    if (cur.kind !== 'field' || next.kind !== 'field') return false;
    if (!next.field.section || cur.field.section === next.field.section) return false;
    setCurrentSection(next.field.section);
    return true;
  };

  const handleNext = async () => {
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    setError('');
    try {
      const liveSeq = buildStepSequence(allFieldsRef.current, responsesRef.current);
      const liveStep = currentStepRef.current;
      const step = liveSeq[liveStep];
      if (!step) return;
      const field = step.field;
      const latestValue = responsesRef.current[field.id];

      if (field.type !== 'document' && latestValue !== undefined) {
        const ok = await saveResponse(field.id, latestValue);
        if (!ok) return;
      }
      const emailOk = await persistRespondentEmail(field, latestValue);
      if (!emailOk) return;

      if (liveStep < liveSeq.length - 1) {
        const nextIndex = liveStep + 1;
        setDirection('forward');
        if (checkSectionTransition(liveSeq, liveStep, nextIndex)) {
          setShowSectionTransition(true);
        } else {
          setCurrentStep(nextIndex);
        }
      }
    } finally {
      navigatingRef.current = false;
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setDirection('backward');
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleContinueAfterTransition = () => {
    setShowSectionTransition(false);
    setDirection('forward');
    setCurrentStep(currentStep + 1);
  };

  const handleSubmit = async () => {
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const liveSeq = buildStepSequence(allFieldsRef.current, responsesRef.current);
      const liveStep = currentStepRef.current;
      const step = liveSeq[liveStep];
      const submitValue = step?.kind === 'field' ? responsesRef.current[step.field.id] : undefined;
      if (step?.kind === 'field' && step.field.type !== 'document' && submitValue !== undefined) {
        const ok = await saveResponse(step.field.id, submitValue);
        if (!ok) return;
      }
      const systemField = allFieldsRef.current.find((f) => f.is_system);
      if (systemField) {
        const systemValue = responsesRef.current[systemField.id];
        const emailOk = await persistRespondentEmail(systemField, systemValue);
        if (!emailOk) return;
      }
      if (!previewMode) {
        const currentRequest = requestRef.current;
        if (!currentRequest) {
          setError('Une erreur est survenue');
          return;
        }
        const { error: statusError } = await supabase
          .from('requests')
          .update({ status: 'completed', completed_at: new Date().toISOString() })
          .eq('id', currentRequest.id);
        if (statusError) throw statusError;

        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/convert-documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            request_id: currentRequest.id,
            access_token: currentRequest.access_token,
          }),
        }).then(async (resp) => {
          if (!resp.ok) {
            const bodyText = await resp.text();
            console.warn(`Document conversion trigger failed (${resp.status}):`, bodyText);
          }
        }).catch((err) => console.warn('Document conversion trigger failed:', err));

        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-form-completed`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            request_id: currentRequest.id,
            access_token: currentRequest.access_token,
          }),
        }).then(async (resp) => {
          if (!resp.ok) {
            const bodyText = await resp.text();
            console.warn(`Notification trigger failed (${resp.status}):`, bodyText);
          }
        }).catch((err) => console.warn('Notification trigger failed:', err));
      }
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting:', err);
      captureError(err, { hook: 'useClientForm', action: 'handleSubmit', requestId: requestRef.current?.id });
      setError('Une erreur est survenue lors de la soumission');
    } finally {
      setSubmitting(false);
      navigatingRef.current = false;
    }
  };

  const isAnyFileUploading = (fieldId: string): boolean => {
    return (uploadedFiles[fieldId] || []).some((f) => f.uploading);
  };

  return {
    loading,
    submitting,
    isSaving,
    uploading: Object.values(uploadedFiles).some((arr) => arr.some((f) => f.uploading)),
    request,
    logoUrl,
    senderDisplayName,
    allFields,
    currentStep,
    direction,
    showWelcome,
    showSectionTransition,
    currentSection,
    responses,
    uploadedFiles,
    error,
    submitted,
    showQuitScreen,
    stepSequence,
    isLastStep,
    canProceed,
    isAnyFileUploading,
    setShowWelcome,
    setShowQuitScreen,
    setError,
    loadPreviewData,
    loadRequest,
    handleResponseChange,
    handleFileSelect,
    handleFileRemove,
    retryFileUpload,
    handleNext,
    handlePrevious,
    handleContinueAfterTransition,
    handleSubmit,
  };
}
